import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box } from '@iheartjane/reefer';
import type { ReservationMode } from '@iheartjane/types';
import { useEffect, useRef } from 'react';

import { useApplyLocalPromoCode } from '@jane/monolith/components/cart/cartFacade/useApplyLocalPromoCode';
import { useUrlPromoCode } from '@jane/monolith/components/cart/cartFacade/useCartEntryParams';
import { useCartState } from '@jane/monolith/components/cart/cartFacade/useCartState';
import { useCustomerDispatch } from '@jane/monolith/customer/redux';
import { analyzeSpecialsSuccess } from '@jane/monolith/customer/redux/cart';
import { useValidatePromoCode } from '@jane/shared/data-access';

import { SubmittableTextInput } from './SubmittableTextInput';

interface PromoCodesProps {
  groupSpecialId?: string | number;
  headless?: boolean;
  reservationMode: ReservationMode;
}

export const PromoCodes = ({
  groupSpecialId,
  headless,
  reservationMode,
}: PromoCodesProps) => {
  const useV3Cart = !!useFlag(FLAGS.c3UseV3Cart);
  const { cart, deliveryAddress } = useCartState();
  const dispatch = useCustomerDispatch();
  const { consumeUrlPromoCode, urlPromoCode } = useUrlPromoCode();
  const v3Promo = useApplyLocalPromoCode(useV3Cart);
  const hasAutoSubmitted = useRef(false);

  const v1Promo = useValidatePromoCode({
    onSuccess: (data) => {
      if (data.isValid) {
        dispatch(analyzeSpecialsSuccess({ cart: data.cart }));
      }
    },
  });

  const validateV1PromoCode = (promoCode: string) =>
    v1Promo.mutate({
      promoCode,
      reservationMode,
      userGroupSpecialId: groupSpecialId,
      uuid: cart?.uuid ?? '',
      deliveryAddress: deliveryAddress ?? undefined,
    });

  //dispatches will be updated when cart redux store is removed
  useEffect(() => {
    if (!useV3Cart && urlPromoCode) {
      validateV1PromoCode(urlPromoCode);
    }
  }, []);

  useEffect(() => {
    const lastPromoCode = v1Promo.variables?.promoCode;
    if (useV3Cart || !lastPromoCode) return;

    validateV1PromoCode(lastPromoCode);
  }, [cart?.uuid, reservationMode]);

  // Waits for a cart, then submits `urlPromoCode` once and consumes it.
  useEffect(() => {
    if (!useV3Cart || !urlPromoCode || hasAutoSubmitted.current) return;

    if (v3Promo.apply(urlPromoCode)) {
      hasAutoSubmitted.current = true;
      consumeUrlPromoCode();
    }
  }, [useV3Cart, urlPromoCode, v3Promo.cartUuid]);

  const updatePromoCode = (value?: string) => {
    if (useV3Cart) {
      if (!value) {
        v3Promo.reset();
      } else if (!v3Promo.apply(value)) {
        // No cart yet: nothing sent, so `urlPromoCode` isn't spent either.
        return;
      }
      consumeUrlPromoCode();
      return;
    }

    if (value) {
      validateV1PromoCode(value);
    } else {
      v1Promo.reset();
    }
    consumeUrlPromoCode();
  };

  const validated = useV3Cart
    ? v3Promo.isSuccess || v3Promo.isError
    : v1Promo.isSuccess;
  const isValid = useV3Cart ? v3Promo.isSuccess : v1Promo.data?.isValid;

  return (
    <Box pl={headless ? 0 : 40}>
      <SubmittableTextInput
        name="promo_code"
        placeholder="Enter code here"
        submitLabel="Apply"
        onReset={() => updatePromoCode(undefined)}
        onSubmit={(value) => updatePromoCode(value)}
        validated={validated}
        hasValidationError={!isValid}
        validationMessage={
          isValid
            ? 'Success! Code will be applied if cart is eligible.'
            : 'Not Valid'
        }
      />
    </Box>
  );
};
